
import { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Link } from 'react-router-dom';
import { usePets } from '@/hooks/usePets';
import { useFeedPosts } from '@/hooks/usePosts';
import { useTranslation } from 'react-i18next';
import { translateSpeciesSingular } from '@/utils/speciesTranslation';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal = ({ isOpen, onClose }: SearchModalProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{
    pets: any[];
    posts: any[];
  }>({
    pets: [],
    posts: []
  });

  const { pets } = usePets();
  const { data: posts } = useFeedPosts();

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults({ pets: [], posts: [] });
      return;
    }

    const query = searchQuery.toLowerCase();
    
    // Search through pets
    const filteredPets = pets?.filter(pet => 
      pet.name?.toLowerCase().includes(query) ||
      pet.breed?.toLowerCase().includes(query) ||
      pet.species?.toLowerCase().includes(query)
    ) || [];

    // Search through posts
    const filteredPosts = posts?.filter(post => 
      post.caption?.toLowerCase().includes(query) ||
      post.pet?.name?.toLowerCase().includes(query)
    ) || [];

    setSearchResults({
      pets: filteredPets.slice(0, 5),
      posts: filteredPosts.slice(0, 5)
    });
  }, [searchQuery, pets, posts]);

  const handleClose = () => {
    setSearchQuery('');
    setSearchResults({ pets: [], posts: [] });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search Petius
          </DialogTitle>
          <DialogDescription>
            Search for pets, posts, and users across the platform
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search for pets, posts, or users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              autoFocus
            />
          </div>

          {searchQuery && (
            <div className="space-y-4">
              {/* Pets Results */}
              {searchResults.pets.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Pets</h3>
                  <div className="space-y-2">
                    {searchResults.pets.map((pet) => (
                      <Link
                        key={pet.id}
                        to={`/pet/${pet.id}`}
                        onClick={handleClose}
                        className="block"
                      >
                        <Card className="hover:bg-gray-50 transition-colors">
                          <CardContent className="p-3">
                            <div className="flex items-center space-x-3">
                              <Avatar className="h-10 w-10">
                                <AvatarImage src={pet.photo_url} alt={pet.name} />
                                <AvatarFallback>
                                  {pet.name?.charAt(0)?.toUpperCase() || 'P'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{pet.name}</p>
                                <p className="text-sm text-gray-500">
                                  {pet.breed} • {translateSpeciesSingular(pet.species, t)}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Posts Results */}
              {searchResults.posts.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Posts</h3>
                  <div className="space-y-2">
                    {searchResults.posts.map((post) => (
                      <Link
                        key={post.id}
                        to={`/post/${post.id}`}
                        onClick={handleClose}
                        className="block"
                      >
                        <Card className="hover:bg-gray-50 transition-colors">
                          <CardContent className="p-3">
                            <div className="flex items-center space-x-3">
                              <div className="h-10 w-10 bg-gray-200 rounded flex items-center justify-center">
                                <Search className="h-5 w-5 text-gray-400" />
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium line-clamp-1">
                                  {post.caption || 'Memory post'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  by {post.pet?.name || 'Unknown pet'}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {searchQuery && 
               searchResults.pets.length === 0 && 
               searchResults.posts.length === 0 && (
                <div className="text-center py-8">
                  <Search className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                  <h3 className="text-lg font-medium text-gray-600">No results found</h3>
                  <p className="text-gray-500">
                    Try searching for different keywords
                  </p>
                </div>
              )}
            </div>
          )}

          {!searchQuery && (
            <div className="text-center py-8 text-gray-500">
              <Search className="mx-auto h-12 w-12 text-gray-300 mb-3" />
              <p>Start typing to search for pets and posts</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;
